import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

//import images
import {
  getBarDetail,
  postBarInformation,
  putBarInformation,
} from "store/bar-list/thunk";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_BAR_LIST_OPEN_STATUS,
  COLOR_BAR_LIST_PARTNER_STATUS,
  COLOR_BAR_LIST_PLAN,
  LIST_OPTION_BAR_LIST_OPEN_STATUS,
  LIST_OPTION_BAR_LIST_PARTNER_STATUS,
  LIST_OPTION_BAR_LIST_PLAN,
} from "helpers/constans";
import UploadImage from "components/Common/UploadImage";
import NaverMap from "components/Common/NaverMap";
import userApi from "api/userApi";
import SearchAddress from "components/Common/SearchAddress";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

export interface Props {
  id?: string;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  LIST_OPTION_ROLE?: any[];
  isShowDetail?: boolean;
  setLinkImage?: any;
  setIsOpen?: any;
  setItemToEdit?: any;
  loadTableData?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const BarForm = ({
  id = "",
  triggerRefresh,
  LIST_OPTION_ROLE = [],
  isShowDetail = false,
  setLinkImage,
  setIsOpen,
  setItemToEdit,
  loadTableData,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isOpenSearchAddress, setIsOpenSearchAddress] = useState<boolean>(false);
  const [isLoadingMap, setLoadingMap] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);
      const data = {
        ...values,
        is_active: values?.is_active === 1 ? true : false,
        nickname: values?.title,
        thumbnail_url: values?.thumbnail_url?.[0].url,
      };

      const response: any = id
        ? await putBarInformation(id, data)
        : await postBarInformation(data);
      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required(`${t("Name is required")}`),
    address: Yup.string().required(`${t("Address is required")}`),
    thumbnail_url: Yup.array()
      .min(1, `${t("This field is required")}`)
      .required(`${t("This field is required")}`),
    background_url: Yup.array()
      .min(1, `${t("This field is required")}`)
      .required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      phone: "",
      address: "",
      short_address: "",
      region1: "",
      region2: "",
      region3: "",
      lat: "",
      long: "",
      files: null,
      background_url: null,
      thumbnail_url: null,
      type: "",
      plan: LIST_OPTION_BAR_LIST_PLAN[0]?.value,
      is_active: 1,
      partner_status: LIST_OPTION_BAR_LIST_PARTNER_STATUS[1]?.value,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    formik.setValues(valueDefault);
  };

  const handleCallBarDetail = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getBarDetail(idItem);

      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  const getLatLngByAddress = async (address: string) => {
    if (address) {
      const data: any = await userApi.getGEOByAddress({
        query: encodeURIComponent(address),
      });

      let dataAddress = data?.data?.addresses;

      if (dataAddress?.length > 0) {
        const responseAddress = dataAddress?.[0];
        let address = "";

        if (responseAddress.address) {
          address = responseAddress.address;
        } else if (responseAddress.jibunAddress) {
          address = responseAddress.jibunAddress;
        } else if (responseAddress.roadAddress) {
          address = responseAddress.roadAddress;
        } else if (responseAddress.englishAddress) {
          address = responseAddress.englishAddress;
        } else if (responseAddress.jibunAddressEnglish) {
          address = responseAddress.jibunAddressEnglish;
        }

        if (address) {
          formik?.setFieldValue("address", address);
        }

        const sido = responseAddress?.addressElements.find((i: any) =>
          i?.types.includes("SIDO")
        );

        const sigungu = responseAddress?.addressElements.find((i: any) =>
          i?.types.includes("SIGUGUN")
        );

        const dongmyun = responseAddress?.addressElements.find((i: any) =>
          i?.types.includes("DONGMYUN")
        );

        const roadname = responseAddress?.addressElements.find((i: any) =>
          i?.types.includes("ROAD_NAME")
        );

        const short_address = [sigungu?.shortName, roadname?.shortName].join(" ");

        formik?.setFieldValue("short_address", short_address);
        formik?.setFieldValue("region1", sido?.longName || '');
        formik?.setFieldValue("region2", sigungu?.longName || '');
        formik?.setFieldValue("region3", dongmyun?.longName || '');

        if (responseAddress?.x || responseAddress?.y) {
          formik?.setFieldValue("lat", responseAddress?.y);
          formik?.setFieldValue("long", responseAddress?.x);
        }
      }
    }
  };

  const handleCloseModalAddress = () => {
    setIsOpenSearchAddress(false);
  };

  const handleOpenSearchAddress = () => {
    setIsOpenSearchAddress(true);
  };

  const onSetValueAddress = async (data: any) => {
    if (data) {
      setLoadingMap(true);
      const res = await getLatLngByAddress(data);
      setLoadingMap(false);
    }
    setIsOpenSearchAddress(false);
  };

  useEffect(() => {
    handleCallBarDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Bar Create")} - ${t("Bars")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Store Information") : t("Create Store Information")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                100 - (100 / 2) * Number(Object.keys(formik.errors).length)
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              {isLoadingDetail && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 3,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: "rgb(164 164 164 / 36%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Spinner size="sm" color="primary"></Spinner>
                </div>
              )}
              <form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="name-bar" className="form-label">
                        {t("Store Name")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="name-bar"
                        name="title"
                        type="text"
                        className="form-control"
                        placeholder={t("Enter store name")}
                        value={formik?.values?.title}
                        onChange={(event: any) =>
                          formik.setFieldValue("title", event?.target?.value)
                        }
                      />
                      {formik.touched.title && formik.errors.title ? (
                        <div className="text-danger mt-2">
                          {formik.errors.title}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="phone" className="form-label">
                        {t("Phone Number")}
                      </Label>
                      <Input
                        name="phone"
                        type="text"
                        className="form-control"
                        id="phone"
                        placeholder={t("Enter phone number")}
                        value={formik?.values?.phone}
                        onChange={(event: any) =>
                          formik.setFieldValue("phone", event?.target?.value)
                        }
                      />
                      {formik.touched.phone && formik.errors.phone ? (
                        <div className="text-danger mt-2">
                          {formik.errors.phone}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Status")}</Label>

                      <DropdownStatusNew
                        name="status"
                        dataList={LIST_OPTION_BAR_LIST_OPEN_STATUS || []}
                        placeholder={`${t("Open Status")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.is_active}
                        onChangeSelect={(event: any) =>
                          formik.setFieldValue("is_active", event?.value)
                        }
                        isHasOptionAll={false}
                        optionAll={{ label: t("All Filter"), value: "" }}
                        colors={COLOR_BAR_LIST_OPEN_STATUS}
                      />

                      {formik?.touched?.plan && formik?.errors?.plan ? (
                        <div className="text-danger mt-2">
                          {/* {formik?.errors?.plan} */}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Plan")}</Label>

                      <DropdownStatusNew
                        name="plan"
                        dataList={LIST_OPTION_BAR_LIST_PLAN || []}
                        placeholder={`${t("Plan")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.plan || null}
                        onChangeSelect={(event: any) =>
                          formik.setFieldValue("plan", event?.value)
                        }
                        isHasOptionAll={false}
                        optionAll={{ label: t("All Filter"), value: "" }}
                        colors={COLOR_BAR_LIST_PLAN}
                      />
                      {formik?.touched?.plan && formik?.errors?.plan ? (
                        <div className="text-danger mt-2">
                          {/* {formik?.errors?.plan} */}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Partner Status")}
                      </Label>

                      <DropdownStatusNew
                        name="partner_status"
                        dataList={LIST_OPTION_BAR_LIST_PARTNER_STATUS || []}
                        placeholder={`${t("Partner Status")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.partner_status}
                        onChangeSelect={(event: any) =>
                          formik.setFieldValue("partner_status", event?.value)
                        }
                        isHasOptionAll={false}
                        optionAll={{ label: t("All Filter"), value: "" }}
                        colors={COLOR_BAR_LIST_PARTNER_STATUS}
                      />

                      {formik?.touched?.plan && formik?.errors?.plan ? (
                        <div className="text-danger mt-2">
                          {/* {formik?.errors?.plan} */}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="address" className="form-label">
                        {t("Address")}
                        <span className="text-danger"> *</span>
                      </Label>

                      <div className="d-flex mb-2 gap-2">
                        <Input
                          name="address"
                          type="text"
                          className="form-control"
                          id="address"
                          placeholder={t("Enter address")}
                          value={formik?.values?.address}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "address",
                              event?.target?.value
                            )
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-primary whitespace-nowrap"
                          onClick={handleOpenSearchAddress}
                        >
                          {t("Find Address")}
                        </button>
                      </div>

                      {formik.touched.address && formik.errors.address ? (
                        <div className="text-danger mt-2">
                          {formik.errors.address}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="address" className="form-label">
                        {t("Short Address")}
                      </Label>
                      <Input
                        name="short_address"
                        type="text"
                        className="form-control"
                        id="short_address"
                        placeholder={t("Enter short address")}
                        value={formik?.values?.short_address}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "short_address",
                            event?.target?.value
                          )
                        }
                      />
                      {/*  {formik.touched.short_address &&
                      formik.errors.short_address ? (
                        <div className="text-danger mt-2">
                          {formik.errors.short_address}
                        </div>
                      ) : null} */}
                    </div>
                  </Col>

                  <Col lg={3}>
                    <div className="mb-3">
                      <Label htmlFor="address" className="form-label">
                        {t("Latitude")}
                      </Label>
                      <Input
                        name="lat"
                        type="text"
                        className="form-control"
                        id="lat"
                        placeholder={t("Enter latitude")}
                        value={formik?.values?.lat}
                        onChange={(event: any) =>
                          formik.setFieldValue("lat", event?.target?.value)
                        }
                      />
                    </div>
                  </Col>

                  <Col lg={3}>
                    <div className="mb-3">
                      <Label htmlFor="address" className="form-label">
                        {t("Longitude")}
                      </Label>
                      <Input
                        name="long"
                        type="text"
                        className="form-control"
                        id="long"
                        placeholder={t("Enter longitude")}
                        value={formik?.values?.long}
                        onChange={(event: any) =>
                          formik.setFieldValue("long", event?.target?.value)
                        }
                      />
                    </div>
                  </Col>

                  <Col lg={4}>
                    <div className="mb-3">
                      <Label htmlFor="region1" className="form-label">
                        {t("Province/Metropolitan City")}
                      </Label>
                      <Input
                        name="region1"
                        type="text"
                        className="form-control"
                        id="region1"
                        placeholder={t("Enter Province/Metropolitan City")}
                        value={formik?.values?.region1}
                        onChange={(event: any) =>
                          formik.setFieldValue("region1", event?.target?.value)
                        }
                      />
                    </div>
                  </Col>

                  <Col lg={4}>
                    <div className="mb-3">
                      <Label htmlFor="region2" className="form-label">
                        {t("City/District")}
                      </Label>
                      <Input
                        name="region2"
                        type="text"
                        className="form-control"
                        id="region2"
                        placeholder={t("Enter City/District")}
                        value={formik?.values?.region2}
                        onChange={(event: any) =>
                          formik.setFieldValue("region2", event?.target?.value)
                        }
                      />
                    </div>
                  </Col>

                  <Col lg={4}>
                    <div className="mb-3">
                      <Label htmlFor="region3" className="form-label">
                        {t("Town/Neighborhood")}
                      </Label>
                      <Input
                        name="region3"
                        type="text"
                        className="form-control"
                        id="region3"
                        placeholder={t("Enter Town/Neighborhood")}
                        value={formik?.values?.region3}
                        onChange={(event: any) =>
                          formik.setFieldValue("region3", event?.target?.value)
                        }
                      />
                    </div>
                  </Col>

                  <Col lg={12}>
                    {isLoadingMap && (
                      <div className="d-flex justify-content-center align-items-center">
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 48 }} spin />
                          }
                        />
                      </div>
                    )}
                    {formik?.values?.lat &&
                      formik?.values?.long &&
                      !isLoadingMap && (
                        <div className="mb-3">
                          <NaverMap
                            nameAddress={formik?.values?.address}
                            latDefault={formik?.values?.lat}
                            lngDefault={formik?.values?.long}
                          />
                        </div>
                      )}
                  </Col>

                  <Col xxl={12}>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("Thumbnail")}
                        <span className="text-danger"> *</span>
                      </label>

                      <UploadImage
                        data={formik.getFieldProps("thumbnail_url")?.value}
                        //setLinkImage={setLinkImage}
                        formik={formik}
                        isMultiple={false}
                        formikNameImage="thumbnail_url"
                      />

                      {formik.errors.thumbnail_url ? (
                        <div className="text-danger mt-2">
                          {formik.errors.thumbnail_url}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xxl={12}>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("Background")}
                        <span className="text-danger"> *</span>
                      </label>

                      <UploadImage
                        data={formik.getFieldProps("background_url")?.value}
                        //setLinkImage={setLinkImage}
                        formik={formik}
                        formikNameImage="background_url"
                      />
                      {formik.errors.background_url ? (
                        <div className="text-danger mt-2">
                          {formik.errors.background_url}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xxl={12}>
                    <div className="mb-3">
                      <label className="form-label">{t("Photos")}</label>

                      <UploadImage
                        data={formik.getFieldProps("files")?.value}
                        setLinkImage={setLinkImage}
                        formik={formik}
                        formikNameImage="files"
                      />
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="hstack gap-2 justify-content-end">
                      {!isShowDetail && (
                        <button type="submit" className="btn btn-primary fs-14">
                          {isLoading ? (
                            <Spinner size="sm" className="me-2"></Spinner>
                          ) : !!id ? (
                            <i className="ri-login-circle-line align-bottom me-2 "></i>
                          ) : (
                            <i className="ri-add-fill align-bottom me-2"></i>
                          )}
                          {id
                            ? t("Button Update Store")
                            : t("Button Create Store")}
                        </button>
                      )}
                    </div>
                  </Col>
                </Row>
              </form>
            </>
          </div>
        </ModalBody>

        {isOpenSearchAddress && (
          <Modal
            isOpen={!!isOpenSearchAddress}
            centered={true}
            size="lg"
            scrollable={false}
          >
            <ModalHeader toggle={handleCloseModalAddress}>
              {"Search Address"}
            </ModalHeader>
            <ModalBody>
              <SearchAddress onSetValueAddress={onSetValueAddress} />
            </ModalBody>
          </Modal>
        )}
      </Modal>
    </React.Fragment>
  );
};

export default BarForm;
